import type { Campaign, CampaignNumber, Did, LeadList, Queue } from "@/lib/types";
import { isTone, storeAudioUuid } from "./audio.js";

/**
 * A campaign as the console shows and edits it: the vocabularies and their words, the bounds, and
 * the arithmetic between a form and `PUT /campaigns/{id}` or `PUT /queues/{id}`.
 *
 * The vocabularies mirror `services/shared/database/models/campaigns.py`, `dialing.py` and
 * `dispositions.py` in `platform`; the bounds mirror `_CampaignSettings` and `LineSettings` in
 * `core-api/src/schemas.py`. The server decides every one of them. The copies here keep a value it
 * would refuse off the form and say why before a round trip does, and a value this console does not
 * know is shown as itself rather than hidden. Pure, for `test/campaign.test.ts`.
 */

export type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "outline";

export interface Term {
  label: string;
  description: string;
}

/** Shared with `line.ts`, which words its own vocabularies the same way. */
export function unknown(value: string): Term {
  return { label: value, description: "Not a value this console knows." };
}

// --- the campaign ------------------------------------------------------------------------------

export const CAMPAIGN_STATUSES = ["draft", "active", "paused", "done"] as const;

const STATUS: Record<string, Term & { tone: Tone }> = {
  draft: { label: "Draft", description: "Being set up. The dialer does not call it.", tone: "outline" },
  active: {
    label: "Active", description: "The dialer calls its leads while a dial mode is on.", tone: "success",
  },
  paused: { label: "Paused", description: "The dialer stops until it is active again.", tone: "warning" },
  done: { label: "Done", description: "Finished. The dialer does not call it.", tone: "neutral" },
};

export function statusText(status: string): Term & { tone: Tone } {
  return STATUS[status] ?? { ...unknown(status), tone: "outline" };
}

/**
 * The modes the dialer runs (ADR-019). The server's vocabulary also holds `progressive` and
 * `predictive_shadow` for later; they are not offered, and a campaign already set to one shows it.
 */
export const DIAL_MODES = ["off", "preview", "power"] as const;

const DIAL_MODE: Record<string, Term> = {
  off: { label: "Off", description: "The dialer does not call this campaign's leads." },
  preview: {
    label: "Preview",
    description: "A Ready rep is offered a lead, sees who it is, and chooses Dial or Skip.",
  },
  power: {
    label: "Power",
    description: "The dialer calls a lead for each Ready rep and connects that rep when the lead answers.",
  },
  progressive: { label: "Progressive", description: "Not run by the dialer yet." },
  predictive_shadow: { label: "Predictive (shadow)", description: "Not run by the dialer yet." },
};

export function dialModeText(mode: string): Term {
  return DIAL_MODE[mode] ?? unknown(mode);
}

export const LANGUAGE_ROUTING = ["off", "soft", "hard"] as const;

const LANGUAGE: Record<string, Term> = {
  off: { label: "Off", description: "A lead's language does not decide who takes the call." },
  soft: { label: "Prefer", description: "A rep who speaks the lead's language is tried first." },
  hard: {
    label: "Require",
    description: "Only a rep who speaks the lead's language, until the line's relax timer lifts the rule.",
  },
};

export function languageRoutingText(value: string): Term {
  return LANGUAGE[value] ?? unknown(value);
}

export const SAFE_HARBOR_ACTIONS = ["hangup", "announcement"] as const;

const SAFE_HARBOR: Record<string, Term> = {
  hangup: { label: "Hang up", description: "The call ends." },
  announcement: {
    label: "Play an announcement, then hang up",
    description: "The recording says who is calling and gives a number to call back.",
  },
};

export function safeHarborText(action: string): Term {
  return SAFE_HARBOR[action] ?? unknown(action);
}

/**
 * Answering machine detection on the calls the dialer places (ADR-104 in `platform`). Premium is
 * the detector still being built: shown, so an admin sees what is coming, and not selectable,
 * because core-api refuses it until that detector is in.
 */
export const AMD_MODES = ["off", "basic", "premium"] as const;
/** `AMD_MODES_AVAILABLE` in `platform`: the modes a campaign can be saved with today. */
export const AMD_MODES_AVAILABLE: readonly string[] = ["off", "basic"];

const AMD_MODE: Record<string, Term> = {
  off: {
    label: "Off",
    description: "The rep is rung the moment the lead's phone answers, and hears whatever answered.",
  },
  basic: {
    label: "Basic",
    description: "The dialer listens to each answer and tells a voicemail greeting from a person by sound.",
  },
  premium: {
    label: "Premium",
    description: "Our own detector, which also tells call screeners and phone menus from a person.",
  },
};

export function amdModeText(mode: string): Term {
  return AMD_MODE[mode] ?? unknown(mode);
}

/** Whether a campaign can be saved with the mode today. A mode this console has no word for is
 *  the server's to judge, so it is not refused here. */
export function amdModeAvailable(mode: string): boolean {
  return AMD_MODES_AVAILABLE.includes(mode) || !(AMD_MODES as readonly string[]).includes(mode);
}

/** The TSR's call-abandonment safe harbor: an unanswered call rings 15 seconds, or four rings,
 *  before the dialer may hang it up. `MIN_DIAL_TIMEOUT_SECONDS` in `platform`. */
export const MIN_DIAL_TIMEOUT_SECONDS = 15;

/** What a campaign is for (ADR-060). `both` is what one nobody labelled reads as. */
export const CAMPAIGN_PURPOSES = ["inbound", "outbound", "both"] as const;
export type CampaignPurpose = (typeof CAMPAIGN_PURPOSES)[number];

export function purposeText(purpose: string): { label: string; description: string } {
  switch (purpose) {
    case "inbound":
      return {
        label: "Inbound",
        description: "Takes calls on its numbers. Its reps are not given leads to dial.",
      };
    case "outbound":
      return {
        label: "Outbound",
        description: "Works dial lists. Answered leads still wait on its line for a rep.",
      };
    default:
      return { label: "Inbound and outbound", description: "Does both, and shows every setting." };
  }
}

/** The tabs on a campaign, in the order they are shown. */
export const CAMPAIGN_TABS = [
  "routing", "numbers", "staffing", "dispositions", "dialing", "cadence", "lists",
] as const;
export type CampaignTab = (typeof CAMPAIGN_TABS)[number];

/** The one tab that only means something to a campaign that dials.
 *
 * Lists is NOT on this list, and the reason is the whole point. An inbound campaign's list is
 * what identifies its callers: `core-api/src/lookup.py` answers the ACD with "the newest lead
 * with this number that the DIALED campaign's own list holds", which is the 2026-09-23 fix for a
 * test lead's name appearing on a live caller's card. It is also what a disposition on an inbound
 * call moves — with no row for `(campaign, lead)` an outcome records nothing (ADR-019, amended) —
 * and what the GHL `lead.enlist` trigger fills. Routing, Staffing and Dispositions are read by
 * both directions too: an answered lead waits on the same line, worked by the same reps, choosing
 * from the same outcomes.
 */
const OUTBOUND_ONLY: readonly CampaignTab[] = ["dialing", "cadence"];

/**
 * Which tabs a campaign shows (ADR-060).
 *
 * An inbound campaign hides the dialing half, which is the confusion this removes. The exception
 * is the one that matters: a campaign labelled inbound that still has a dial mode is DIALING, and
 * hiding the settings that govern it would leave an admin unable to see — let alone stop — calls
 * their floor is placing. A wrong label must never hide live behaviour, so the tabs come back and
 * `dialingContradictsPurpose` puts a warning on the page.
 */
export function visibleTabs(
  campaign: Pick<Campaign, "purpose" | "dial_mode">,
): readonly CampaignTab[] {
  if (campaign.purpose !== "inbound" || campaign.dial_mode !== "off") return CAMPAIGN_TABS;
  return CAMPAIGN_TABS.filter((t) => !OUTBOUND_ONLY.includes(t));
}

/** Whether the campaign dials despite saying it is inbound — what the warning is about. */
export function dialingContradictsPurpose(
  campaign: Pick<Campaign, "purpose" | "dial_mode">,
): boolean {
  return campaign.purpose === "inbound" && campaign.dial_mode !== "off";
}

/** Inclusive `[min, max]` per field, as `_CampaignSettings` has them. The attempts a lead gets,
 *  the waits between them and the daily cap on a number are not here: they are the cadence's
 *  (ADR-067; `CADENCE_BOUNDS` in `lib/cadence.ts`). */
/** `CALLING_HOURS` in core-api: a lead may be called only from 8 am to 9 pm on their own clock
 *  (TCPA), so a campaign's window sits inside these, on one day (ACL-237). */
export const CALLING_HOURS = ["08:00", "21:00"] as const;
/** `SAFE_HARBOR_SECONDS` in core-api: the TSR safe harbor's two seconds, the default wait. A wait
 *  an admin sets over it is warned on, not refused (ACL-633). */
export const SAFE_HARBOR_SECONDS = 2;
/** `MAX_ABANDON_AFTER_SECONDS` in core-api: the longest an admin may make an answered lead wait
 *  for a rep (ACL-633). */
export const MAX_ABANDON_AFTER_SECONDS = 15;

/** The warning under the wait when an admin sets more than the safe harbor's two seconds. */
export function abandonWaitWarning(seconds: number): string | null {
  if (!Number.isFinite(seconds) || seconds <= SAFE_HARBOR_SECONDS) return null;
  return (
    `A lead who answered may hear up to ${seconds} seconds of silence before a rep is on the ` +
    `line. The safe harbor allows ${SAFE_HARBOR_SECONDS} seconds after the lead's greeting ends, ` +
    "and this wait is counted from the answer, so you are allowing for the greeting. Calls still " +
    "dropped after it count as abandoned. Make sure this fits your compliance policy."
  );
}

export const DIALING_BOUNDS = {
  dial_priority: [0, 100],
  dial_timeout_seconds: [MIN_DIAL_TIMEOUT_SECONDS, 120],
  preview_timeout_seconds: [10, 900],
  abandon_after_seconds: [1, MAX_ABANDON_AFTER_SECONDS],
  list_horizon_days: [0, 365],
} as const;

/** What core-api would refuse about a calling window, on the field it belongs to, or null. The
 *  same rule and the same reason, said before Save (ACL-237). */
export function windowProblem(
  start: string,
  end: string,
): { field: "calling_window_start" | "calling_window_end"; message: string } | null {
  const [opens, closes] = CALLING_HOURS;
  const rule = "a lead may be called only from 8 am to 9 pm on their own clock";
  if (start < opens || start > closes) {
    return { field: "calling_window_start", message: `Between ${opens} and ${closes}: ${rule}` };
  }
  if (end < opens || end > closes) {
    return { field: "calling_window_end", message: `Between ${opens} and ${closes}: ${rule}` };
  }
  if (start >= end) {
    return { field: "calling_window_end", message: `After the start, on the same day: ${rule}` };
  }
  return null;
}

/**
 * What a calling window means, read the way the dialer reads it (`eligibility.Window`): the end is
 * exclusive, a start after the end runs across midnight, and a start equal to the end is the whole
 * day. `outsideTcpa` flags any hour outside the TCPA's 8 am to 9 pm on the called party's clock.
 */
export function windowNote(
  start: string,
  end: string,
  leadClock: boolean,
): { text: string; outsideTcpa: boolean } {
  const clock = leadClock ? "each lead's own clock" : "the campaign's clock";
  if (start === end) {
    return { text: `The whole day: leads may be called at any hour, on ${clock}.`, outsideTcpa: true };
  }
  const overnight = start > end;
  return {
    text: overnight
      ? `From ${start} until ${end} the next morning, on ${clock}.`
      : `From ${start} until ${end}, on ${clock}.`,
    outsideTcpa: overnight || start < "08:00" || end > "21:00",
  };
}

/** `_MEDIA_RE` in core-api's schemas: a `local_stream://` name, or a sound file path — how a setting
 *  named its sound before the audio store, and still may. A store file is its URL (`lib/audio.ts`). */
const MEDIA = /^(local_stream:\/\/[A-Za-z0-9_-]{1,64}|[A-Za-z0-9][A-Za-z0-9._/-]{0,255})$/;

/** Null for an empty value (the setting's default) or one the server takes. */
export function mediaProblem(value: string): string | null {
  const v = value.trim();
  return v === "" || isTone(v) || MEDIA.test(v) || storeAudioUuid(v) !== null
    ? null
    : "Choose a file from the audio store";
}

/** The zones a US campaign's leads live in. A saved zone outside the list still shows. */
export const US_TIMEZONES: readonly { zone: string; label: string }[] = [
  { zone: "America/New_York", label: "Eastern" },
  { zone: "America/Chicago", label: "Central" },
  { zone: "America/Denver", label: "Mountain" },
  { zone: "America/Phoenix", label: "Arizona" },
  { zone: "America/Los_Angeles", label: "Pacific" },
  { zone: "America/Anchorage", label: "Alaska" },
  { zone: "Pacific/Honolulu", label: "Hawaii" },
];

/** The dialing form. Strings are `""` for a setting left to its fallback. */
export interface DialingValues {
  /** The cadence the campaign runs (ADR-067): the only policy for how often a lead is called.
   *  Null is taken only while the mode is Off. */
  cadence_id: number | null;
  dial_mode: string;
  /** Which of a rep's dialing campaigns gives them their next lead (ADR-054). */
  dial_priority: number;
  dial_timeout_seconds: number;
  preview_timeout_seconds: number;
  calling_window_start: string;
  calling_window_end: string;
  respect_lead_timezone: boolean;
  default_timezone: string;
  abandon_after_seconds: number;
  safe_harbor_action: string;
  safe_harbor_media: string;
  /** Whether the dialer listens for a machine before it rings the rep (ADR-104). */
  amd_mode: string;
  /** Whether that listening only suggests (ADR-106). */
  amd_passive: boolean;
  outbound_gateway_ids: number[];
  caller_id_number: string;
  /** A rep with their own number on the campaign presents it (ADR-037). */
  rep_number_caller_id: boolean;
  list_horizon_days: number;
}

export function dialingValues(c: Campaign): DialingValues {
  return {
    cadence_id: c.cadence_id,
    dial_mode: c.dial_mode,
    // `?? 0` for the minutes between deploying this console and the core-api that sends the
    // field: without it the form opens invalid ("Enter a priority") on a campaign nobody touched.
    dial_priority: c.dial_priority ?? 0,
    dial_timeout_seconds: c.dial_timeout_seconds,
    preview_timeout_seconds: c.preview_timeout_seconds,
    calling_window_start: c.calling_window_start,
    calling_window_end: c.calling_window_end,
    respect_lead_timezone: c.respect_lead_timezone,
    default_timezone: c.default_timezone ?? "",
    abandon_after_seconds: c.abandon_after_seconds,
    safe_harbor_action: c.safe_harbor_action,
    safe_harbor_media: c.safe_harbor_media ?? "",
    // `?? "off"` for the minutes between deploying this console and the core-api that sends the
    // field: without it an untouched form would read as changed and save a mode nobody chose.
    amd_mode: c.amd_mode ?? "off",
    // Passive is what the server gives a campaign nobody set, so it is what a missing field reads as.
    amd_passive: c.amd_passive ?? true,
    outbound_gateway_ids: [...c.outbound_gateway_ids],
    caller_id_number: c.caller_id_number ?? "",
    rep_number_caller_id: c.rep_number_caller_id ?? false,
    list_horizon_days: c.list_horizon_days,
  };
}

const CLEARABLE = new Set<string>(["default_timezone", "safe_harbor_media", "caller_id_number"]);

/**
 * The settings the form changed, as `PUT /campaigns/{id}` takes them, and nothing else. The server
 * writes only what is sent, so an untouched setting is never rewritten: a form nobody changed sends
 * nothing, a cleared fallback goes as null rather than "", and the trunks go only when their order
 * did.
 */
export function dialingChanges(values: DialingValues, campaign: Campaign): Record<string, unknown> {
  const before = dialingValues(campaign);
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(values) as (keyof DialingValues)[]) {
    const now = values[key];
    const was = before[key];
    if (Array.isArray(now) && Array.isArray(was) ? sameOrder(now, was) : now === was) continue;
    if (CLEARABLE.has(key)) out[key] = (now as string).trim() || null;
    else out[key] = now;
  }
  return out;
}

/** The server's answer (a 422) to a campaign saved with a dial mode and no cadence (ADR-067). */
export const NEEDS_CADENCE = "A campaign that dials needs a cadence";

/**
 * Whether the form asks the dialer to call with no cadence to say how (ADR-067): a mode other than
 * Off and no cadence chosen. The cadence is the only dialing policy, so the server refuses this;
 * the form says it on the field first and does not submit.
 */
export function needsCadence(values: Pick<DialingValues, "dial_mode" | "cadence_id">): boolean {
  return values.dial_mode !== "off" && values.cadence_id === null;
}

export interface CallerIdChoice {
  number: string;
  description: string | null;
  /** False for the saved number once it is not one the campaign may present (released, moved,
   *  switched off, inbound only): kept, so the form opens showing what is in effect rather than
   *  silently saving a change nobody made. */
  held: boolean;
}

/** The one rule for a campaign's primary number, on the Dialing tab's Caller ID and the Numbers
 *  tab's Make primary alike, and what core-api checks before it makes one primary: a campaign
 *  route to this campaign, so a lead who calls it back reaches it (ADR-066); the campaign's, not a
 *  rep's (ADR-037); switched on, so it rings; and not inbound only (platform ADR-202). */
export function mayBePrimary(n: {
  routed_here: boolean;
  sip_endpoint_id: number | null;
  enabled: boolean;
  inbound_only_at?: string | null;
}): boolean {
  return n.routed_here && n.sip_endpoint_id == null && n.enabled && !n.inbound_only_at;
}

/** What a campaign may present: the numbers `mayBePrimary` allows, and the saved one whatever
 *  became of it. A saved number that reaches the campaign through a queue route to its line still
 *  works as one (a callback rings the line), so it counts as held and says so. Never another
 *  tenant's, which core-api refuses even from a superadmin. */
export function callerIdChoices(
  dids: readonly (Pick<
    Did, "number" | "description" | "tenant_id" | "campaign_id" | "sip_endpoint_id" | "enabled"
  > & Partial<Pick<Did, "inbound_only_at" | "action_type" | "action_target">>)[],
  campaign: Pick<Campaign, "id" | "tenant_id" | "queue_name">,
  saved: string | null,
): CallerIdChoice[] {
  const ours = dids.filter((d) => d.tenant_id === campaign.tenant_id);
  const own = ours
    .filter((d) => mayBePrimary({ ...d, routed_here: d.campaign_id === campaign.id }))
    .map((d) => ({ number: d.number, description: d.description, held: true }))
    .sort((a, b) => a.number.localeCompare(b.number));
  if (saved && !own.some((c) => c.number === saved)) {
    const viaLine = ours.find(
      (d) =>
        d.number === saved &&
        d.action_type === "queue" &&
        campaign.queue_name != null &&
        d.action_target === campaign.queue_name &&
        mayBePrimary({ ...d, routed_here: true }),
    );
    own.push(
      viaLine
        ? { number: saved, description: "Reaches the campaign through its line", held: true }
        : { number: saved, description: null, held: false },
    );
  }
  return own;
}

// --- a campaign's numbers (ACL-629, platform ADR-202) --------------------------------------------

export const NUMBER_ROLES = ["primary", "local", "rep"] as const;

/** A campaign's numbers by role, each in number order: the order the Numbers tab lists them. */
export function numbersByRole<T extends Pick<CampaignNumber, "number" | "role">>(
  numbers: readonly T[],
): Record<CampaignNumber["role"], T[]> {
  const out: Record<CampaignNumber["role"], T[]> = { primary: [], local: [], rep: [] };
  for (const n of [...numbers].sort((a, b) => a.number.localeCompare(b.number))) out[n.role].push(n);
  return out;
}

/** Where a number is local to, as the dialer reads it: its area code and its state. */
export function numberPlace(n: Pick<CampaignNumber, "area_code" | "region">): string {
  return [n.area_code, n.region].filter(Boolean).join(" · ") || "—";
}

/** A number's standing on the tab: presented, or inbound only and why. */
export function numberStanding(
  n: Pick<CampaignNumber, "inbound_only_reason" | "inbound_only_at" | "enabled">,
): { label: string; description: string; tone: "success" | "warning" | "neutral" } {
  if (!n.enabled) {
    return { label: "Off", description: "Switched off on the Numbers page: it does not ring.", tone: "neutral" };
  }
  if (!n.inbound_only_at) {
    return { label: "Active", description: "Rings the campaign and is presented on calls.", tone: "success" };
  }
  return n.inbound_only_reason === "replaced"
    ? {
        label: "Inbound only · replaced",
        description: "Another number took its place. Leads it called can still call it back; no call presents it.",
        tone: "warning",
      }
    : {
        label: "Inbound only · flagged",
        description: "Flagged as spam. Leads it called can still call it back; no call presents it.",
        tone: "warning",
      };
}

// --- a rep's own number on the campaign (ADR-037) ---------------------------------------------

export const REP_NUMBER_FALLBACKS = ["line", "rep_only"] as const;

const REP_NUMBER_FALLBACK: Record<string, Term> = {
  line: {
    label: "Anyone on the line, after a wait",
    description: "Then the line's other reps may take the caller, who keeps their place in line.",
  },
  rep_only: {
    label: "Only that rep",
    description:
      "Then the call ends and nobody else is rung. Add a closing message to play first.",
  },
};

export function repNumberFallbackText(fallback: string): Term {
  return REP_NUMBER_FALLBACK[fallback] ?? unknown(fallback);
}

/** Mirrors `rep_number_wait_seconds` in `_CampaignSettings`. */
export const REP_NUMBER_WAIT_BOUNDS: readonly [number, number] = [5, 600];

/** The campaign's rule for its rep numbers in a sentence: what the route dialog and a rep's number
 *  say happens when that rep cannot take the call. The closing message is optional, so a campaign
 *  without one ends the call instead of playing anything. */
export function repNumberRule(
  c: Pick<Campaign, "rep_number_fallback" | "rep_number_wait_seconds" | "rep_number_message">,
): string {
  const seconds = c.rep_number_wait_seconds ?? 20;
  if (c.rep_number_fallback !== "rep_only") return `anyone on the line after ${seconds} seconds`;
  const ending = c.rep_number_message ? "the closing message" : "the call ends";
  return `only that rep, for up to ${seconds} seconds, then ${ending}`;
}

export interface RepNumberValues {
  rep_number_fallback: string;
  rep_number_wait_seconds: number;
  rep_number_message: string;
}

export function repNumberValues(c: Campaign): RepNumberValues {
  return {
    rep_number_fallback: c.rep_number_fallback ?? "line",
    rep_number_wait_seconds: c.rep_number_wait_seconds ?? 20,
    rep_number_message: c.rep_number_message ?? "",
  };
}

/** The rule's fields the form changed, as `PUT /campaigns/{id}` takes them. */
export function repNumberChanges(values: RepNumberValues, c: Campaign): Record<string, unknown> {
  const before = repNumberValues(c);
  const out: Record<string, unknown> = {};
  if (values.rep_number_fallback !== before.rep_number_fallback) {
    out.rep_number_fallback = values.rep_number_fallback;
  }
  if (values.rep_number_wait_seconds !== before.rep_number_wait_seconds) {
    out.rep_number_wait_seconds = values.rep_number_wait_seconds;
  }
  if (values.rep_number_message.trim() !== before.rep_number_message) {
    out.rep_number_message = values.rep_number_message.trim() || null;
  }
  return out;
}

/** What core-api would refuse in the rule, said before the round trip: a wait out of bounds, or
 *  longer than the line lets anyone wait. "Only that rep" needs no closing message; without one
 *  the call simply ends (ADR-037). */
export function repNumberProblem(
  values: RepNumberValues,
  line: Pick<Queue, "max_wait_seconds"> | null,
): string | null {
  const [low, high] = REP_NUMBER_WAIT_BOUNDS;
  const wait = values.rep_number_wait_seconds;
  if (!Number.isInteger(wait) || wait < low || wait > high) {
    return `The wait is between ${low} and ${high} seconds.`;
  }
  if (line?.max_wait_seconds && wait > line.max_wait_seconds) {
    return (
      `The line lets a caller wait at most ${line.max_wait_seconds} seconds, so the wait for ` +
      "their rep cannot be longer."
    );
  }
  return null;
}

// --- a text to a rep's own number (ADR-095) ----------------------------------------------------

export const REP_TEXT_FALLBACKS = ["campaign", "rep_only"] as const;

const REP_TEXT_FALLBACK: Record<string, Term> = {
  campaign: {
    label: "That rep, then the campaign inbox",
    description:
      "The rep sees it at once. If it is still unread after the wait, the campaign's inbox shows it too.",
  },
  rep_only: {
    label: "Only that rep",
    description: "Nobody else on the campaign sees it, however long it waits.",
  },
};

export function repTextFallbackText(fallback: string): Term {
  return REP_TEXT_FALLBACK[fallback] ?? unknown(fallback);
}

/** Mirrors `rep_text_wait_seconds` in `_CampaignSettings` (0 to a day), in the minutes the form
 *  asks for. 0 shares a text with the campaign's inbox at once. */
export const REP_TEXT_WAIT_MINUTES: readonly [number, number] = [0, 24 * 60];

export interface RepTextValues {
  rep_text_fallback: string;
  /** The wait, in minutes. */
  wait_minutes: number;
}

export function repTextValues(
  c: Pick<Campaign, "rep_text_fallback" | "rep_text_wait_seconds">,
): RepTextValues {
  return {
    rep_text_fallback: c.rep_text_fallback ?? "campaign",
    wait_minutes: Math.round((c.rep_text_wait_seconds ?? 300) / 60),
  };
}

/** Whether two states of the form are the same rule. An emptied wait (NaN) is equal to itself. */
export function sameRepText(a: RepTextValues, b: RepTextValues): boolean {
  return a.rep_text_fallback === b.rep_text_fallback && Object.is(a.wait_minutes, b.wait_minutes);
}

/** The rule's fields the form changed, as `PUT /campaigns/{id}` takes them. The wait is compared in
 *  minutes, as it is shown, so a wait saved as 90 seconds is not sent back as 120. Only that rep
 *  has no wait, so none is sent with it. */
export function repTextChanges(
  values: RepTextValues,
  c: Pick<Campaign, "rep_text_fallback" | "rep_text_wait_seconds">,
): Record<string, unknown> {
  const before = repTextValues(c);
  const out: Record<string, unknown> = {};
  if (values.rep_text_fallback !== before.rep_text_fallback) out.rep_text_fallback = values.rep_text_fallback;
  if (values.rep_text_fallback !== "rep_only" && values.wait_minutes !== before.wait_minutes) {
    out.rep_text_wait_seconds = values.wait_minutes * 60;
  }
  return out;
}

/** What core-api would refuse in the rule, said before the round trip. */
export function repTextProblem(values: RepTextValues): string | null {
  const [low, high] = REP_TEXT_WAIT_MINUTES;
  const wait = values.wait_minutes;
  if (!Number.isInteger(wait) || wait < low || wait > high) {
    return `The wait is between ${low} and ${high} minutes.`;
  }
  return null;
}

/** The campaign's rule for a text to a rep's own number, in a sentence. */
export function repTextRule(c: Pick<Campaign, "rep_text_fallback" | "rep_text_wait_seconds">): string {
  if ((c.rep_text_fallback ?? "campaign") === "rep_only") return "only that rep sees it";
  const seconds = c.rep_text_wait_seconds ?? 300;
  if (seconds === 0) return "that rep and the campaign's inbox see it at once";
  return `that rep sees it, and the campaign's inbox too once it has waited ${formatSpan(seconds)} unread`;
}

/** Each rep's own numbers on this campaign, by their phone's id, from the numbers the console has
 *  listed: campaign routes that name a rep. The whole row comes back, because Staffing both shows
 *  a number and takes it back, and taking it back writes to it by id. */
export function repNumbers<
  T extends Pick<Did, "number" | "action_type" | "campaign_id" | "sip_endpoint_id">,
>(dids: readonly T[], campaignId: string): Map<number, T[]> {
  const out = new Map<number, T[]>();
  for (const d of dids) {
    if (d.action_type !== "campaign" || d.campaign_id !== campaignId || d.sip_endpoint_id == null) {
      continue;
    }
    const held = [...(out.get(d.sip_endpoint_id) ?? []), d];
    held.sort((a, b) => a.number.localeCompare(b.number));
    out.set(d.sip_endpoint_id, held);
  }
  return out;
}

/** The numbers a rep can be given: the tenant's, routed nowhere yet, and not held for a rep who
 *  has been invited but not yet joined (ACL-176). */
export function unroutedNumbers<
  T extends Pick<Did, "number" | "action_type" | "tenant_id"> & Partial<Pick<Did, "held_for">>,
>(
  dids: readonly T[],
  tenantId: number,
): T[] {
  return dids
    .filter((d) => d.action_type === null && d.tenant_id === tenantId && !d.held_for)
    .sort((a, b) => a.number.localeCompare(b.number));
}

// --- dispositions ------------------------------------------------------------------------------

const DISPOSITION_KIND: Record<string, string> = {
  terminal: "Ends the cadence",
  callback: "Call back",
  retry: "Try again later",
  hold: "Hold",
};

export function dispositionKindText(kind: string): string {
  return DISPOSITION_KIND[kind] ?? kind;
}

// --- lists and leads ---------------------------------------------------------------------------

/** `MAX_UPLOAD_BYTES` in core-api's lists router. */
export const MAX_LIST_BYTES = 5 * 1024 * 1024;

/** Said before the upload rather than after it. */
export function listFileProblem(file: { name: string; size: number }): string | null {
  if (!/\.(csv|txt)$/i.test(file.name)) return "Choose a CSV file.";
  if (file.size === 0) return "The file is empty.";
  if (file.size > MAX_LIST_BYTES) return "A list is at most 5 MB. Split the file and import each part.";
  return null;
}

/** `Collins leads - Sept.csv` → `Collins leads - Sept`. */
export function listNameFromFile(filename: string): string {
  return filename.replace(/\.[^.]+$/, "").trim().slice(0, 200) || filename;
}

const LIST_STATUS: Record<string, Term & { tone: Tone }> = {
  importing: { label: "Importing", description: "The file is being read into leads.", tone: "accent" },
  ready: { label: "Ready", description: "Imported. Its leads are on the campaign.", tone: "success" },
  failed: { label: "Failed", description: "The import stopped.", tone: "danger" },
};

export function listStatusText(status: string): Term & { tone: Tone } {
  return LIST_STATUS[status] ?? { ...unknown(status), tone: "outline" };
}

/** What an import made of its file, in one line. */
export function listOutcome(
  list: Pick<LeadList, "status" | "total_rows" | "imported" | "duplicates" | "skipped" | "error">,
): string {
  if (list.status === "importing") {
    return `Reading ${list.total_rows.toLocaleString()} row${list.total_rows === 1 ? "" : "s"}…`;
  }
  if (list.status === "failed") return list.error ?? "The import failed.";
  const parts = [`${list.imported.toLocaleString()} imported`];
  if (list.duplicates > 0) parts.push(`${list.duplicates.toLocaleString()} already on the campaign`);
  if (list.skipped > 0) parts.push(`${list.skipped.toLocaleString()} skipped`);
  return parts.join(" · ");
}

const SKIP_REASON: Record<string, string> = {
  bad_phone: "no usable phone number",
  bad_consent_date: "a consent date that is not a date",
  no_consent: "no consent date",
  dnc: "on the do-not-call list",
  sms_only: "asked to be texted, not called",
};

/** Why rows were skipped, the biggest reason first: `no consent date (12), on the do-not-call
 *  list (3)`. A reason this console does not know is shown as the server spelled it. */
export function skipSummary(reasons: Readonly<Record<string, number>>): string {
  return countsSummary(reasons, (reason) => SKIP_REASON[reason] ?? reason.replaceAll("_", " "));
}

const UNREAD: Record<string, string> = {
  bad_state: "state",
  bad_language: "language",
  bad_email: "email",
};

/** What an import's leads were made without because the file's value could not be read
 *  (ACL-490), the most first: `language (2), state (1)`. The leads were imported; this is not a
 *  skip. A kind this console does not know is shown as the server spelled it, less its `bad_`. */
export function unreadSummary(warnings: Readonly<Record<string, number>>): string {
  return countsSummary(warnings, (kind) => UNREAD[kind] ?? kind.replace(/^bad_/, "").replaceAll("_", " "));
}

function countsSummary(counts: Readonly<Record<string, number>>, label: (key: string) => string): string {
  return Object.entries(counts)
    .filter(([, rows]) => rows > 0)
    .sort(([a, x], [b, y]) => y - x || a.localeCompare(b))
    .map(([key, rows]) => `${label(key)} (${rows})`)
    .join(", ");
}

/** `campaign_leads.state`. Only `fresh` and `retrying` are dialable. */
export const LEAD_STATES = [
  "fresh", "offered", "in_flight", "retrying", "needs_disposition", "done", "dnc", "exhausted",
  "retired", "blocked",
] as const;

/**
 * The states a lead rests in that the dialer never calls again, until a person or a list recycle moves
 * it. `needs_disposition` rests too, but it is waiting on a rep's outcome, not finished.
 */
const NOT_CALLED_AGAIN: ReadonlySet<string> = new Set(["done", "dnc", "exhausted", "retired", "blocked"]);

const LEAD_STATE: Record<string, Term & { tone: Tone }> = {
  fresh: { label: "Fresh", description: "Not called yet.", tone: "accent" },
  offered: { label: "Offered", description: "Offered to a rep, waiting for Dial or Skip.", tone: "accent" },
  in_flight: { label: "Calling", description: "A call to the lead is up.", tone: "accent" },
  retrying: { label: "Retrying", description: "Called before; waiting for its next try.", tone: "neutral" },
  needs_disposition: {
    label: "Needs a disposition", description: "The call ended without an outcome.", tone: "warning",
  },
  done: { label: "Done", description: "Worked to an outcome that needs no more calls.", tone: "success" },
  dnc: { label: "Do not call", description: "On the do-not-call list.", tone: "danger" },
  exhausted: {
    label: "Out of attempts", description: "Called as many times as allowed.", tone: "neutral",
  },
  retired: { label: "Retired", description: "Past the list horizon.", tone: "neutral" },
  blocked: { label: "Blocked", description: "The compliance check refused it.", tone: "danger" },
};

export function leadStateText(state: string): Term & { tone: Tone } {
  return LEAD_STATE[state] ?? { ...unknown(state), tone: "outline" };
}

export function leadName(lead: { first_name: string | null; last_name: string | null }): string | null {
  return [lead.first_name, lead.last_name].filter(Boolean).join(" ").trim() || null;
}

/**
 * When the dialer may call a lead on a list again: its next try, or the end of its hold when that is
 * later. A hold is a trigger's (an appointment booked) or a rep's hold disposition's, and a release
 * ends it early. A lead the dialer will not call again has neither, whatever the row still holds: an
 * Appointment set hold followed by Signed leaves the lead done with the hold in place.
 */
export function nextTry(
  lead: { state: string; next_attempt_at: string | null; hold_until: string | null },
  now: Date = new Date(),
): { at: string | null; held: boolean } {
  if (NOT_CALLED_AGAIN.has(lead.state)) return { at: null, held: false };
  const hold = lead.hold_until ? Date.parse(lead.hold_until) : Number.NaN;
  if (!Number.isFinite(hold) || hold <= now.getTime()) return { at: lead.next_attempt_at, held: false };
  const next = lead.next_attempt_at ? Date.parse(lead.next_attempt_at) : Number.NaN;
  if (Number.isFinite(next) && next > hold) return { at: lead.next_attempt_at, held: false };
  return { at: lead.hold_until, held: true };
}

// --- small arithmetic --------------------------------------------------------------------------

export function sameOrder(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/** The list with one item swapped for its neighbour; unchanged when either end is out of range. */
export function moveItem<T>(items: readonly T[], index: number, by: -1 | 1): T[] {
  const next = [...items];
  const target = index + by;
  if (index < 0 || index >= next.length || target < 0 || target >= next.length) return next;
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}

/** A span for a hint beside a seconds field, the two largest units: `1 min 30 s`, `1 d 1 h`. */
export function formatSpan(totalSeconds: number): string {
  let rest = Math.max(0, Math.round(totalSeconds));
  const parts: string[] = [];
  for (const [size, unit] of [[86_400, "d"], [3_600, "h"], [60, "min"], [1, "s"]] as const) {
    const n = Math.floor(rest / size);
    rest -= n * size;
    if (n > 0) parts.push(`${n} ${unit}`);
  }
  return parts.length === 0 ? "0 s" : parts.slice(0, 2).join(" ");
}
